import { Link, useSearchParams } from 'react-router-dom'
import type { DayUsage } from '../../api/types'
import { PROVIDER_NAMES, ProviderUsageTable, TaskUsageTable, formatMs } from '../../components/admin/UsageTables'
import { BarChart } from '../../components/BarChart'
import { Card, ErrorNote, LoadingBlock, Stat } from '../../components/ui'
import { useAnalytics } from '../../hooks/useAdmin'
import { formatCredits, formatMicroDollars, shortDay } from '../../utils/format'

const PERIODS = [7, 30, 90, 365]


/** Spend, charges and revenue over time, by task, job type and workspace. `?workspace=` narrows it to one. */
export function AdminUsagePage() {
  const [params, setParams] = useSearchParams()
  const days = Number(params.get('days')) || 30
  const workspaceId = params.get('workspace') ?? undefined
  const analytics = useAnalytics(days, workspaceId)
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value == null) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }
  return (
    <div className="stack-lg">
      <div className="row between">
        <div className="row">
          <select value={days} onChange={(event) => setParam('days', event.target.value)} aria-label="Period">
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                Last {p} days
              </option>
            ))}
          </select>
          {workspaceId && (
            <span className="chip">
              <Link to={`/studio/admin/workspaces/${workspaceId}`}>One workspace</Link>
              <button type="button" className="tx-link" onClick={() => setParam('workspace', null)} aria-label="Show every workspace">
                ×
              </button>
            </span>
          )}
        </div>
        {analytics.data && (
          <span className="muted small">
            Rolled up through {analytics.data.rolled_through ?? 'nothing yet'}; later days are live. Dates are UTC.
          </span>
        )}
      </div>
      {analytics.isPending ? <LoadingBlock /> : analytics.isError ? <ErrorNote error={analytics.error} /> : <Report analytics={analytics.data} scoped={!!workspaceId} />}
    </div>
  )
}

function Report({ analytics, scoped }: { analytics: NonNullable<ReturnType<typeof useAnalytics>['data']>; scoped: boolean }) {
  const { totals, days, tasks, providers, workspaces, job_types } = analytics
  const dollars = (point: DayUsage) => ({
    spend: point.provider_cost_micros,
    credits: point.credits_charged / 1000,
    revenue: point.revenue_cents * 10_000,
  })
  // Whoever actually billed us this period, named rather than assumed, most spend first.
  const billed = providers.filter((p) => p.provider_cost_micros !== 0).map((p) => PROVIDER_NAMES[p.provider] ?? p.provider)
  const smallest = billed.pop()
  const spentOn = smallest == null ? 'Nothing billed yet' : billed.length === 0 ? smallest : `${billed.join(', ')} and ${smallest}`
  return (
    <>
      <div className="grid-4">
        <Stat
          label="Provider spend"
          value={formatMicroDollars(totals.provider_cost_micros)}
          note={totals.unpriced_calls > 0 ? `${totals.unpriced_calls} calls to unpriced models not counted` : spentOn}
        />
        <Stat label="Credits used" value={formatCredits(totals.credits_charged)} note={`Worth ${formatMicroDollars(totals.credits_value_micros)} at $0.001 each`} />
        <Stat label="Margin" value={formatMicroDollars(totals.margin_micros)} note="Credits' worth less provider spend" />
        <Stat label="Revenue" value={formatMicroDollars(totals.revenue_cents * 10_000)} note="Stripe payments received" />
      </div>
      <div className="grid-4">
        <Stat label="AI calls" value={totals.calls.toLocaleString('en-US')} note={`${totals.failures} failed`} />
        <Stat label="Jobs finished" value={totals.jobs_succeeded.toLocaleString('en-US')} note={`${totals.jobs_failed} failed`} />
        <Stat label="Signups" value={scoped ? '—' : totals.signups.toLocaleString('en-US')} note={scoped ? 'Not per workspace' : 'New accounts'} />
        <Stat label="Period" value={`${days.length} days`} note={`${shortDay(analytics.from)} to ${shortDay(analytics.to)}`} />
      </div>
      <div className="grid-2">
        <Card title="Spend, credits and revenue">
          <BarChart
            data={days}
            label={(d) => shortDay(d.day)}
            format={formatMicroDollars}
            series={[
              { label: 'Provider spend', color: 'var(--danger)', value: (d) => dollars(d).spend },
              { label: 'Credits used', color: 'var(--accent)', value: (d) => dollars(d).credits },
              { label: 'Revenue', color: 'var(--ok)', value: (d) => dollars(d).revenue },
            ]}
          />
        </Card>
        <Card title="Jobs">
          <BarChart
            data={days}
            stacked
            label={(d) => shortDay(d.day)}
            format={(n) => n.toLocaleString('en-US')}
            series={[
              { label: 'Succeeded', color: 'var(--ok)', value: (d) => d.jobs_succeeded },
              { label: 'Failed', color: 'var(--danger)', value: (d) => d.jobs_failed },
            ]}
          />
        </Card>
      </div>
      <Card title="By task">
        <TaskUsageTable tasks={tasks} />
      </Card>
      <Card title="By provider">
        <ProviderUsageTable providers={providers} />
      </Card>
      <div className="grid-2">
        <Card title="Job types" actions={<Link to="/studio/admin/jobs">Open the queue</Link>}>
          {job_types.length === 0 ? (
            <p className="muted">No jobs finished in this period.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th className="num">Done</th>
                    <th className="num">Failed</th>
                    <th className="num">Median</th>
                    <th className="num">95th</th>
                  </tr>
                </thead>
                <tbody>
                  {job_types.map((j) => (
                    <tr key={j.job_type}>
                      <td>
                        <code>{j.job_type}</code>
                      </td>
                      <td className="num">{j.succeeded}</td>
                      <td className={`num ${j.failed > 0 ? 'error-text' : ''}`}>{j.failed}</td>
                      <td className="num">{j.p50_ms == null ? '—' : formatMs(j.p50_ms)}</td>
                      <td className="num">{j.p95_ms == null ? '—' : formatMs(j.p95_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {!scoped && (
          <Card title="Workspaces charged the most">
            {workspaces.length === 0 ? (
              <p className="muted">No usage in this period.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th className="num">Calls</th>
                      <th className="num">Spend</th>
                      <th className="num">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspaces.map((w) => (
                      <tr key={w.workspace_id ?? 'none'}>
                        <td>
                          {w.workspace_id ? <Link to={`/studio/admin/workspaces/${w.workspace_id}`}>{w.name ?? 'Deleted workspace'}</Link> : <span className="muted">No workspace</span>}
                        </td>
                        <td className="num">{w.calls}</td>
                        <td className="num">{formatMicroDollars(w.provider_cost_micros)}</td>
                        <td className="num">{formatCredits(w.credits_charged, 'exact')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  )
}
